import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LangContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Buildings, Eye, EyeSlash, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError(t("reset.invalid"));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success(t("reset.success.title"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("reset.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f8f6] flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 flex items-center justify-center bg-[#b25a38] text-white">
              <Buildings size={22} weight="duotone" />
            </span>
            <div className="leading-tight">
              <div className="serif text-lg font-bold text-[#1a1a1a]">Warisan Kuching</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#5c5c5c]">
                Heritage archive
              </div>
            </div>
          </div>

          {done ? (
            <div data-testid="reset-success">
              <div className="overline mb-3">{t("reset.overline")}</div>
              <h2 className="serif text-3xl lg:text-4xl font-black text-[#1a1a1a] leading-tight">
                {t("reset.success.title")}
              </h2>
              <div className="mt-6 flex items-start gap-3 border border-[#c9d6a8] bg-[#eef4ea] p-4">
                <CheckCircle size={22} weight="duotone" className="text-[#3f5119] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[#3f5119]">{t("reset.success.body")}</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mt-6 w-full h-11 bg-[#b25a38] text-white text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#93492c] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="reset-goto-signin-btn"
              >
                {t("reset.success.cta")}
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          ) : (
            <>
              <div className="overline mb-3">{t("reset.overline")}</div>
              <h2 className="serif text-3xl lg:text-4xl font-black text-[#1a1a1a] leading-tight">
                {t("reset.title")}
              </h2>
              <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">{t("reset.subtitle")}</p>

              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
                <div>
                  <label className="overline block mb-2">{t("reset.new")}</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-11 px-3 pr-11 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5c5c5c] hover:text-[#1a1a1a]"
                      aria-label="Toggle password"
                    >
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">{t("reset.confirm")}</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                  />
                </div>

                {error && (
                  <div
                    className="border border-[#f0c4be] bg-[#fbe4de] text-[#8a2b1e] text-sm px-3 py-2"
                    data-testid="reset-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full h-11 bg-[#b25a38] text-white text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#93492c] transition-colors duration-150 disabled:opacity-60"
                  data-testid="reset-submit-btn"
                >
                  {loading ? t("reset.submitting") : t("reset.submit")}
                </button>

                <div className="text-center text-sm">
                  <Link to="/login" className="text-[#b25a38] hover:text-[#93492c] hover:underline underline-offset-4 font-semibold">
                    ← {t("login.forgot.back")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
